import { FindGraphComponents, GraphComponent } from "./types";

/**
 * Finds all disconnected components (islands) in the network.
 * Uses a Breadth-First Search (BFS) to group connected nodes.
 */
export const findGraphComponents: FindGraphComponents = ({ topology }) => {
  // Build an Adjacency List for quick lookup
  // We use a Map<Id, Set<Id>> for efficient neighbor checks
  const adjacency = new Map<string, Set<string>>();

  for (const edge of topology.edges) {
    // Initialize sets if missing
    if (!adjacency.has(edge.from)) adjacency.set(edge.from, new Set());
    if (!adjacency.has(edge.to)) adjacency.set(edge.to, new Set());

    // Add bi-directional connection
    adjacency.get(edge.from)!.add(edge.to);
    adjacency.get(edge.to)!.add(edge.from);
  }

  const visited = new Set<string>();
  const components: GraphComponent[] = [];

  for (const nodeId of Object.keys(topology.nodes)) {
    // Skip if we already assigned this node to a component
    if (visited.has(nodeId)) continue;

    // Start a new component
    const currentComponent = new Set<string>();
    const queue: string[] = [nodeId];

    // BFS to find all connected nodes
    while (queue.length > 0) {
      const currentId = queue.shift()!;

      if (visited.has(currentId)) continue;
      visited.add(currentId);
      currentComponent.add(currentId);

      // Add unvisited neighbors to the queue
      const neighbors = adjacency.get(currentId);
      if (neighbors) {
        for (const neighborId of neighbors) {
          if (!visited.has(neighborId)) {
            queue.push(neighborId);
          }
        }
      }
    }

    components.push(currentComponent);
  }

  return components;
};
